import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import { useOrderData } from '../hooks/useOrderData';
import { useLineItems } from '../hooks/useLineItems';
import { LineItemAssignmentDialog } from './dialogs/LineItemAssignmentDialog';
import { BulkAssignmentDialog } from './dialogs/BulkAssignmentDialog';
import { ShippingLineItem } from '../DeShippingTableWrapper';
import { useLocationAssignment } from '../hooks/useLocationAssignment';
import { LineItemStatusBadge } from './LineItemStatusBadge';
import { LineItemFulfillmentLocationTemplate } from './shared/LineItemFulfillmentLocationTemplate';
import { useOrderSelection } from '../hooks/useOrderSelection';
import { useLineItemSelection } from '../hooks/useLineItemSelection';

interface AssignLocationsDialogProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemSelection: ReturnType<typeof useLineItemSelection>;
  visible: boolean;
  onHide: () => void;
}

export const AssignLocationsDialog: React.FC<AssignLocationsDialogProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection,
  visible,
  onHide,
}) => {
  const order = orderSelection.selectedOrder;
  const {
    lineItems,
    loading,
    error,
    refetch: fetchLineItems,
    setSelectedLineItem,
    updating,
  } = lineItemsHook;

  const {
    selectedLineItems,
    clearSelection,
    toggleLineItemSelection,
    selectAllUnassigned,
    getSelectAllState,
    selectedUnassignedCount,
    unassignedItems
  } = lineItemSelection;



  const locationAssignmentHook = useLocationAssignment();
  const [bulkAssignVisible, setBulkAssignVisible] = useState(false);

  const toastRef = React.useRef<Toast>(null);

  // Clear selection when dialog closes
  useEffect(() => {
    if (!visible) {
      clearSelection();
    }
  }, [visible, clearSelection]);

  const handleAssignLocation = (lineItem: ShippingLineItem) => {
    setSelectedLineItem(lineItem);
    locationAssignmentHook.showDialog(lineItem);
  };

  const handleBulkAssign = () => {
    setBulkAssignVisible(true);
  };

  const handleBulkAssignHide = () => {
    setBulkAssignVisible(false);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      selectAllUnassigned();
    } else {
      clearSelection();
    }
  };

  const selectAllTemplate = () => {
    const selectAllState = getSelectAllState();
    
    return (
      <div className="flex align-items-center gap-2">
        <Checkbox
          checked={selectAllState.checked}
          onChange={(e) => handleSelectAll(e.checked || false)}
          disabled={unassignedItems.length === 0 || updating}
          tooltip={unassignedItems.length === 0 ? "No unassigned items to select" : "Select all unassigned items"}
          tooltipOptions={{ position: 'top' }}
          className={selectAllState.indeterminate ? 'p-checkbox-indeterminate' : ''}
        />
      </div>
    );
  };

  const selectionTemplate = (rowData: ShippingLineItem) => {
    const isUnassigned = rowData.fulfillment_status === 'unassigned';
    const isSelected = selectedLineItems.some(item => item.line_item_id === rowData.line_item_id);
    
    return (
      <Checkbox
        checked={isSelected}
        onChange={() => toggleLineItemSelection(rowData)}
        disabled={!isUnassigned || updating}
        tooltip={!isUnassigned ? "Only unassigned items can be selected for bulk assignment" : undefined}
        tooltipOptions={{ position: 'top' }}
      />
    );
  };

  const actionTemplate = (rowData: ShippingLineItem) => {
    const isUnassigned = rowData.fulfillment_status === 'unassigned';
    const buttonId = `assign-btn-${rowData.line_item_id}`;
    
    return (
      <div>
        <Button
          id={buttonId}
          label={updating ? "Updating..." : isUnassigned ? "Assign Location" : "Already Assigned"}
          icon={updating ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
          disabled={updating || !isUnassigned}
          size="small"
          onClick={() => isUnassigned && handleAssignLocation(rowData)}
          className={isUnassigned ? "p-button-outlined" : "p-button-outlined p-button-secondary"}
          tooltip={!isUnassigned ? `This item was already assigned to ${rowData.fulfillment_location} location` : undefined}
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  const getAssignmentStats = () => {
    const assigned = lineItems.filter(item => item.fulfillment_location).length;
    const unassigned = lineItems.length - assigned;
    return { assigned, unassigned, total: lineItems.length };
  };

  const stats = getAssignmentStats();

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <div>
          <div className="mb-4">
            <Skeleton width="200px" height="1.5rem" className="mb-3" />
            <div className="flex gap-4 mb-3">
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="100px" height="1rem" />
              </div>
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="120px" height="1rem" />
              </div>
              <div className="flex align-items-center gap-2 py-2">
                <Skeleton width="30px" height="1.5rem" borderRadius="16px" />
                <Skeleton width="80px" height="1rem" />
              </div>
            </div>
          </div>

          <div>
            <Skeleton width="100px" height="1.25rem" className="mb-3" />
            <div className="border-1 surface-border border-round">
              <div className="p-3 border-bottom-1 surface-border">
                <div className="flex gap-4">
                  <Skeleton width="120px" height="1rem" />
                  <Skeleton width="80px" height="1rem" />
                  <Skeleton width="60px" height="1rem" />
                  <Skeleton width="80px" height="1rem" />
                  <Skeleton width="140px" height="1rem" />
                  <Skeleton width="100px" height="1rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="flex flex-column gap-4">
          <Message 
            severity="error" 
            text={`Failed to load line items: ${error}`}
            className="w-full"
          />
          <div className="flex justify-content-center">
            <Button
              label="Retry"
              icon="pi pi-refresh"
              onClick={fetchLineItems}
              className="p-button-outlined"
            />
          </div>
        </div>
      );
    }

    // Empty state
    if (lineItems.length === 0) {
      return (
        <div className="flex flex-column align-items-center justify-content-center p-6">
          <i className="pi pi-inbox" style={{ fontSize: '3rem', color: 'var(--text-color-secondary)' }}></i>
          <h6 className="mt-3 mb-2">No Line Items Found</h6>
          <p className="text-color-secondary m-0">This order doesn't have any line items to assign locations to.</p>
        </div>
      );
    }

    // Success state with data
    return (
      <div>
        <div className="mb-4">
          <h5>Order: {order?.order_name}</h5>
          <div className="flex gap-4 mb-3">
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.assigned} severity="success" />
              <span className="font-bold px-2">Assigned Items</span>
            </div>
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.unassigned} severity="warning" />
              <span className="font-bold px-2">Unassigned Items</span>
            </div>
            <div className="flex align-items-center gap-2 py-2">
              <Badge value={stats.total} severity="info" />
              <span className="font-bold px-2">Total Items</span>
            </div>
          </div>
          {updating && (
            <Message 
              severity="info" 
              text="Updating line item assignments..." 
              className="w-full mb-3"
            />
          )}
        </div>

        <div>
          <div className="flex justify-content-between align-items-center mb-3">
            <h6 className="m-0 py-3">Line Items</h6>
            {selectedUnassignedCount > 0 && (
              <Button
                label={`Bulk Assign (${selectedUnassignedCount} items)`}
                icon="pi pi-map-marker"
                className="p-button-outlined p-button-primary"
                onClick={handleBulkAssign}
                disabled={updating}
              />
            )}
          </div>
          <DataTable 
            value={lineItems} 
            responsiveLayout="scroll"
            paginator
            rows={10}
            emptyMessage="No line items found"
            loading={updating}
          >
            <Column 
              header={selectAllTemplate}
              body={selectionTemplate}
              style={{ width: '120px' }}
            />
            <Column 
              field="product_name" 
              header="Product Name" 
              sortable
            />
            <Column 
              field="variant_title" 
              header="Variant" 
            />
            <Column 
              field="product_quantity" 
              header="Quantity" 
              style={{ width: '100px' }}
            />
            <Column 
              header="Status" 
              body={(rowData: ShippingLineItem) => <LineItemStatusBadge status={rowData.fulfillment_status || 'unassigned'} />}
              style={{ width: '120px' }}
            />
            <Column 
              header="Assigned Location" 
              body={LineItemFulfillmentLocationTemplate}
            />
            <Column 
              header="Action" 
              body={actionTemplate}
              style={{ width: '150px' }}
            />
          </DataTable>
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Assign Fulfillment Locations"
        visible={visible}
        onHide={onHide}
        style={{ width: '900px' }}
        modal
        data-testid="assign-locations-dialog"
        footer={
          <div>
            <Button 
              label="Refresh" 
              icon="pi pi-refresh" 
              onClick={fetchLineItems}
              className="p-button-outlined mr-2"
              disabled={loading || updating}
            />
            <Button 
              label="Close" 
              icon="pi pi-times" 
              onClick={onHide} 
              className="p-button-text" 
              disabled={updating}
            />
          </div>
        }
      >
        {order ? renderContent() : (
          <Message 
            severity="warn" 
            text="No order selected" 
            className="w-full"
          />
        )}
      </Dialog>

      <LineItemAssignmentDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        locationAssignmentHook={locationAssignmentHook}
        lineItemSelection={lineItemSelection}
      />

      <BulkAssignmentDialog
        visible={bulkAssignVisible}
        onHide={handleBulkAssignHide}
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
      />
    </>
  );
};